import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

// SVG Icon Components
const BrainIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2C9.5 2 7.5 4 7.5 6.5c0 1 .3 1.9.8 2.7C7.1 10.2 6 11.9 6 14c0 2.8 2.2 5 5 5h2c2.8 0 5-2.2 5-5 0-2.1-1.1-3.8-2.3-4.8.5-.8.8-1.7.8-2.7C16.5 4 14.5 2 12 2z" />
    <path d="M9 14c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z" />
  </svg>
);

const LightningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" />
  </svg>
);

const AtomIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="1" />
    <path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5z" />
    <path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5z" />
  </svg>
);

const WaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 12c0-2.5 2-4.5 4.5-4.5S11 9.5 11 12s-2 4.5-4.5 4.5S2 14.5 2 12z" />
    <path d="M13 12c0-2.5 2-4.5 4.5-4.5S22 9.5 22 12s-2 4.5-4.5 4.5S13 14.5 13 12z" />
  </svg>
);

const NetworkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="2" />
    <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49" />
    <path d="M20.07 4.93a10 10 0 0 1 0 14.14m-16.14.01a10 10 0 0 1 0-14.14" />
  </svg>
);

const InfinityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M17.5 12c0 2.8-2.2 5-5 5s-5-2.2-5-5 2.2-5 5-5c1.4 0 2.6.5 3.5 1.5L17.5 12z" />
    <path d="M6.5 12c0-2.8 2.2-5 5-5s5 2.2 5 5-2.2 5-5 5c-1.4 0-2.6-.5-3.5-1.5L6.5 12z" />
  </svg>
);

const CrystalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polygon points="12,2 2,7 12,13 22,7" />
    <polyline points="2,17 12,22 22,17" />
    <polyline points="2,12 12,17 22,12" />
  </svg>
);

const RocketIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const HomePage = () => {
  useEffect(() => {
    // Intersection Observer for fade-in animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <>
      <style>{`
        /* Hero Section */
        .hero {
          padding: 10rem 2rem 8rem;
          background: linear-gradient(to bottom, var(--white), var(--gray-50));
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-container {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6rem;
          align-items: center;
        }

        .hero-content {
          max-width: 600px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.625rem 1.25rem;
          background: var(--gray-100);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 2rem;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.2s forwards;
        }

        .hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          line-height: 1.1;
          color: var(--black);
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.4s forwards;
        }

        .hero-subtitle {
          font-size: 1.375rem;
          font-weight: 400;
          color: var(--gray-600);
          margin-bottom: 2rem;
          line-height: 1.5;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.6s forwards;
        }

        .hero-description {
          font-size: 1.125rem;
          color: var(--gray-600);
          line-height: 1.7;
          margin-bottom: 3rem;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.8s forwards;
        }

        .hero-actions {
          display: flex;
          gap: 1rem;
          opacity: 0;
          animation: fadeInUp 0.8s ease 1s forwards;
        }

        .hero-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--black);
          color: var(--white);
          padding: 1rem 2rem;
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .hero-cta:hover {
          background: var(--gray-800);
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .hero-cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--white);
          color: var(--gray-700);
          padding: 1rem 2rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .hero-cta-secondary:hover {
          border-color: var(--gray-400);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .hero-visual {
          opacity: 0;
          animation: fadeInUp 0.8s ease 1.2s forwards;
        }

        .hero-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: var(--gray-200);
          border-radius: var(--radius-lg);
          overflow: hidden;
          aspect-ratio: 1;
        }

        .grid-item {
          background: var(--white);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gray-400);
          transition: all 0.4s ease;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .grid-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, var(--blue-500), var(--blue-600));
          transition: left 0.3s ease;
          z-index: 1;
        }

        .grid-item:hover::before {
          left: 0;
        }

        .grid-item:hover {
          color: var(--white);
          transform: scale(1.05);
          z-index: 2;
        }

        .grid-item svg {
          width: 32px;
          height: 32px;
          position: relative;
          z-index: 3;
        }

        /* Sections */
        .section-header {
          text-align: center;
          margin-bottom: 5rem;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }

        .section-badge {
          display: inline-flex;
          padding: 0.5rem 1rem;
          background: var(--gray-100);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          color: var(--black);
          margin-bottom: 1.5rem;
          letter-spacing: -0.03em;
          line-height: 1.1;
        }

        .section-description {
          font-size: 1.25rem;
          color: var(--gray-600);
          line-height: 1.6;
        }

        /* Manifesto */
        .manifesto-content {
          max-width: 1000px;
          margin: 0 auto;
        }

        .manifesto-quote {
          background: var(--white);
          border-radius: var(--radius-xl);
          padding: 4rem;
          margin: 4rem 0;
          box-shadow: var(--shadow-md);
          border: 1px solid var(--gray-200);
          position: relative;
        }

        .manifesto-quote::before {
          content: '"';
          position: absolute;
          top: -1rem;
          left: 2rem;
          font-size: 8rem;
          color: var(--gray-200);
          font-family: serif;
          line-height: 1;
        }

        .quote-text {
          font-size: clamp(1.5rem, 3vw, 2.25rem);
          font-weight: 400;
          line-height: 1.4;
          color: var(--gray-900);
          font-style: italic;
          text-align: center;
          margin-bottom: 2rem;
        }

        .quote-highlight {
          color: var(--blue-600);
          font-weight: 600;
          font-style: normal;
        }

        .quote-attribution {
          text-align: center;
          font-size: 1rem;
          color: var(--gray-500);
          font-weight: 500;
        }

        .manifesto-origin {
          background: var(--white);
          border-radius: var(--radius-lg);
          padding: 3rem;
          border: 1px solid var(--gray-200);
          margin-top: 3rem;
        }

        .origin-text {
          font-size: 1.125rem;
          color: var(--gray-700);
          line-height: 1.7;
          text-align: center;
        }

        /* Paradigm */
        .paradigm-container {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 4rem;
          align-items: stretch;
          max-width: 1200px;
          margin: 0 auto;
        }

        .paradigm-card {
          background: var(--white);
          border-radius: var(--radius-xl);
          padding: 3rem;
          border: 1px solid var(--gray-200);
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .paradigm-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-4px);
        }

        .paradigm-card.legacy {
          border-color: #fecaca;
          background: linear-gradient(135deg, #fef2f2, #ffffff);
        }

        .paradigm-card.legacy .paradigm-title {
          color: #dc2626;
        }

        .paradigm-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 2rem;
        }

        .paradigm-subtitle {
          font-size: 1rem;
          color: var(--gray-500);
          margin-bottom: 2rem;
          font-weight: 500;
        }

        .paradigm-list {
          list-style: none;
          flex: 1;
        }

        .paradigm-list li {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.5rem;
          font-size: 1rem;
          color: var(--gray-700);
          line-height: 1.6;
        }

        .paradigm-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #dcfce7;
          color: #16a34a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: bold;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .paradigm-card.legacy .paradigm-icon {
          background: #fecaca;
          color: #dc2626;
        }

        .paradigm-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: var(--gray-400);
          font-weight: 300;
        }

        /* Technology */
        .tech-intro {
          text-align: center;
          max-width: 900px;
          margin: 0 auto 5rem;
        }

        .tech-intro-text {
          font-size: 1.125rem;
          color: var(--gray-600);
          line-height: 1.7;
          margin-bottom: 2rem;
        }

        .tech-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
          gap: 3rem;
        }

        .tech-card {
          background: var(--white);
          border-radius: var(--radius-xl);
          padding: 3rem;
          border: 1px solid var(--gray-200);
          transition: all 0.4s ease;
          text-align: center;
          position: relative;
          overflow: hidden;
          cursor: pointer;
        }

        .tech-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, var(--blue-50), var(--gray-50));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .tech-card:hover::before {
          opacity: 1;
        }

        .tech-card:hover {
          box-shadow: var(--shadow-xl);
          transform: translateY(-8px) scale(1.02);
          border-color: var(--blue-200);
        }

        .tech-icon {
          width: 80px;
          height: 80px;
          border-radius: var(--radius-lg);
          background: var(--gray-100);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          transition: all 0.4s ease;
          position: relative;
          z-index: 2;
        }

        .tech-icon svg {
          width: 40px;
          height: 40px;
          stroke: var(--gray-600);
          transition: all 0.4s ease;
        }

        .tech-card:hover .tech-icon {
          background: var(--blue-500);
          transform: scale(1.1) rotate(5deg);
        }

        .tech-card:hover .tech-icon svg {
          stroke: var(--white);
        }

        .tech-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.75rem;
          position: relative;
          z-index: 2;
        }

        .tech-subtitle {
          font-size: 1rem;
          color: var(--blue-600);
          font-weight: 600;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 2;
        }

        .tech-description {
          color: var(--gray-600);
          line-height: 1.7;
          margin-bottom: 2.5rem;
          font-size: 1rem;
          position: relative;
          z-index: 2;
        }

        .tech-specs {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          position: relative;
          z-index: 2;
        }

        .tech-spec {
          background: var(--gray-50);
          border-radius: var(--radius);
          padding: 1rem;
          flex: 1;
          text-align: center;
        }

        .tech-spec-label {
          font-size: 0.75rem;
          color: var(--gray-500);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }

        .tech-spec-value {
          font-size: 0.875rem;
          color: var(--gray-900);
          font-weight: 700;
        }

        /* Substrate Section */
        .substrate-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6rem;
          align-items: center;
          margin-top: 3rem;
        }

        .substrate-text h3 {
          font-size: 2rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 2rem;
          line-height: 1.2;
        }

        .substrate-text p {
          font-size: 1.125rem;
          color: var(--gray-600);
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .substrate-visual {
          background: var(--gray-100);
          border-radius: var(--radius-xl);
          height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .substrate-diagram {
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, var(--blue-50), var(--gray-50));
          position: relative;
        }

        .substrate-diagram::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, var(--blue-500), transparent 70%);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          opacity: 0.3;
          animation: pulse-diagram 3s ease-in-out infinite alternate;
        }

        @keyframes pulse-diagram {
          from { 
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
          to { 
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.1;
          }
        }

        /* Vision */
        .vision-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .vision-card {
          background: var(--white);
          border-radius: var(--radius-lg);
          padding: 3rem;
          border: 1px solid var(--gray-200);
          transition: all 0.4s ease;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .vision-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 0;
          background: var(--blue-600);
          transition: height 0.3s ease;
        }

        .vision-card:hover::after {
          height: 100%;
        }

        .vision-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-4px) translateX(4px);
          border-color: var(--blue-200);
        }

        .vision-number {
          font-size: 0.875rem;
          color: var(--gray-500);
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .vision-title {
          font-size: 1.375rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 1rem;
          transition: color 0.3s ease;
          line-height: 1.3;
        }

        .vision-card:hover .vision-title {
          color: var(--blue-600);
        }

        .vision-description {
          color: var(--gray-600);
          line-height: 1.7;
          font-size: 1rem;
        }

        /* Protocol */
        .protocol-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .protocol-card {
          background: var(--white);
          border-radius: var(--radius-xl);
          padding: 3rem;
          border: 1px solid var(--gray-200);
          transition: all 0.4s ease;
          text-align: center;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .protocol-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--gray-50), var(--blue-50));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .protocol-card:hover::before {
          opacity: 1;
        }

        .protocol-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-4px) scale(1.02);
          border-color: var(--blue-200);
        }

        .protocol-number {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: var(--black);
          color: var(--white);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 auto 1.5rem;
          transition: all 0.4s ease;
          position: relative;
          z-index: 2;
        }

        .protocol-card:hover .protocol-number {
          background: var(--blue-600);
          transform: scale(1.1) rotate(10deg);
        }

        .protocol-title {
          font-size: 1.375rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 1rem;
          position: relative;
          z-index: 2;
        }

        .protocol-description {
          color: var(--gray-600);
          line-height: 1.7;
          font-size: 1rem;
          position: relative;
          z-index: 2;
        }

        /* Contact */
        .contact-content {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .contact-description {
          font-size: 1.25rem;
          color: var(--gray-600);
          line-height: 1.7;
          margin-bottom: 3rem;
        }

        .contact-actions {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero {
            padding: 8rem 1rem 6rem;
          }
          .hero-container {
            grid-template-columns: 1fr;
            gap: 4rem;
            text-align: center;
          }
          .hero-actions {
            flex-direction: column;
            align-items: center;
          }
          .paradigm-container {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
          .paradigm-arrow {
            transform: rotate(90deg);
            margin: 1rem 0;
          }
          .tech-grid,
          .vision-grid,
          .protocol-grid {
            grid-template-columns: 1fr;
          }
          .substrate-content {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
        }

        @media (max-width: 640px) {
          .hero-title {
            font-size: 2.5rem;
            line-height: 1.1;
          }
          .section-title {
            font-size: 2rem;
            line-height: 1.2;
          }
          .manifesto-quote,
          .tech-card,
          .protocol-card {
            padding: 2rem;
          }
          .tech-specs {
            flex-direction: column;
          }
        }

        @media (max-width: 480px) {
          .hero {
            padding: 6rem 1rem 4rem;
          }
        }
      `}</style>

      {/* Hero Section */}
      <section id="hero" className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              Introducing Electro-Agnostic Distributed Computing
            </div>
            <h1 className="hero-title">
              Computation, Remastered.
            </h1>
            <p className="hero-subtitle">
              We do not build faster machines. We architect a new reality.
            </p>
            <p className="hero-description">
              Angalifu has developed a revolutionary computational paradigm by moving computation from discrete digital logic to the continuous electromagnetic substrate, dissolving the fundamental limits of the digital age to unlock exponential capacity for humanity's most intractable problems.
            </p>
            <div className="hero-actions">
              <Link to="/research" className="hero-cta">
                Read our manifesto
                <span>→</span>
              </Link>
              <Link to="/technology" className="hero-cta-secondary">
                Explore the technology
                <span>↓</span>
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-grid">
              <div className="grid-item"><LightningIcon /></div>
              <div className="grid-item"><BrainIcon /></div>
              <div className="grid-item"><NetworkIcon /></div>
              <div className="grid-item"><AtomIcon /></div>
              <div className="grid-item"><InfinityIcon /></div>
              <div className="grid-item"><WaveIcon /></div>
              <div className="grid-item"><CrystalIcon /></div>
              <div className="grid-item"><RocketIcon /></div>
              <div className="grid-item"><SparkIcon /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto Section */}
      <section id="manifesto" className="section section-alt fade-in">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">Our Foundation</div>
            <h2 className="section-title">The Angalifu Mandate</h2>
            <p className="section-description">
              A declaration of computational independence from the inherited limitations of digital thinking
            </p>
          </div>

          <div className="manifesto-content">
            <div className="manifesto-quote">
              <p className="quote-text">
                "We reject the suffocating fog of cow-pathic impossibility—the inherited lie that some problems are walls, not doors. We are not catching up to Western technology. We are returning to African mathematical foundations. We are not leapfrogging. We are <span className="quote-highlight">rematrixing</span>."
              </p>
              <p className="quote-attribution">— The Angalifu Collective</p>
            </div>

            <div className="manifesto-origin">
              <p className="origin-text">
                Our origin story is not in Silicon Valley. It begins 37,000 years ago with the combinatorial logic of the Lebombo and Ishango Bones—the world's first mathematical instruments. Angalifu scales this ancient African mathematical intuition through the hyper-dimensional fabric of electromagnetic field geometry, returning computation to its physical roots.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Paradigm Section */}
      <section id="paradigm" className="section fade-in">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">The Transformation</div>
            <h2 className="section-title">From Digital Limits to Physical Law</h2>
            <p className="section-description">
              The CAP theorem is not a law of physics—it is an artifact of a brittle, packet-based paradigm. We introduce Electro-Agnostic Distributed Computing (EADC) to transcend this digital straitjacket.
            </p>
          </div>

          <div className="paradigm-container">
            <div className="paradigm-card legacy">
              <h3 className="paradigm-title">The Digital Paradigm</h3>
              <p className="paradigm-subtitle">TCP/IP & Von Neumann Architecture</p>
              <ul className="paradigm-list">
                <li>
                  <span className="paradigm-icon">✗</span>
                  <span>Governed by the Turing Machine abstraction and CAP theorem constraints</span>
                </li>
                <li>
                  <span className="paradigm-icon">✗</span>
                  <span>Computation as a sequence of discrete, serialized logic operations</span>
                </li>
                <li>
                  <span className="paradigm-icon">✗</span>
                  <span>Information reduced to abstract binary 'bits' divorced from physics</span>
                </li>
                <li>
                  <span className="paradigm-icon">✗</span>
                  <span>Noise treated as adversarial interference to be minimized</span>
                </li>
                <li>
                  <span className="paradigm-icon">✗</span>
                  <span><strong>Result:</strong> Fundamental tradeoffs between Consistency, Availability, and Partition Tolerance</span>
                </li>
              </ul>
            </div>

            <div className="paradigm-arrow">→</div>

            <div className="paradigm-card">
              <h3 className="paradigm-title">The EADC Paradigm</h3>
              <p className="paradigm-subtitle">LightFold & Electromagnetic Computing</p>
              <ul className="paradigm-list">
                <li>
                  <span className="paradigm-icon">✓</span>
                  <span>Governed by Maxwell's Equations and Quantum Field Theory</span>
                </li>
                <li>
                  <span className="paradigm-icon">✓</span>
                  <span>Computation as parallel evolution of continuous physical fields</span>
                </li>
                <li>
                  <span className="paradigm-icon">✓</span>
                  <span>Information as physical state ("physit") with measurable properties</span>
                </li>
                <li>
                  <span className="paradigm-icon">✓</span>
                  <span>Noise harvested as computational fuel through coherent interference</span>
                </li>
                <li>
                  <span className="paradigm-icon">✓</span>
                  <span><strong>Result:</strong> Consistency becomes physical law, availability is intrinsic, latency approaches the speed of light</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="section section-alt fade-in">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">Core Innovation</div>
            <h2 className="section-title">The Angalifu EAPU Architecture</h2>
            <p className="section-description">
              A symbiotic dual-pathway processing unit that intelligently routes computational workloads to optimal physical substrates
            </p>
          </div>

          <div className="tech-intro">
            <p className="tech-intro-text">
              The Electro-Agnostic Processing Unit (EAPU) represents a fundamental departure from monolithic processor design. Instead of forcing all computation through identical pathways, the EAPU recognizes that different computational tasks have fundamentally different requirements and routes them accordingly.
            </p>
          </div>

          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-icon">
                <BrainIcon />
              </div>
              <h3 className="tech-title">The Cerebral Cortex</h3>
              <p className="tech-subtitle">Security, Governance & Verification</p>
              <p className="tech-description">
                Handles computations requiring absolute correctness, cryptographic security, and formal verification. Executes full protocol stacks including post-quantum cryptography, homomorphic encryption, and zero-knowledge proof systems with mathematical guarantees.
              </p>
              <div className="tech-specs">
                <div className="tech-spec">
                  <div className="tech-spec-label">Latency</div>
                  <div className="tech-spec-value">μs - ms</div>
                </div>
                <div className="tech-spec">
                  <div className="tech-spec-label">Guarantees</div>
                  <div className="tech-spec-value">Formal</div>
                </div>
                <div className="tech-spec">
                  <div className="tech-spec-label">Security</div>
                  <div className="tech-spec-value">Post-Quantum</div>
                </div>
              </div>
            </div>

            <div className="tech-card">
              <div className="tech-icon">
                <LightningIcon />
              </div>
              <h3 className="tech-title">The Spinal Cord</h3>
              <p className="tech-subtitle">Reflex Speed & Massive Parallelism</p>
              <p className="tech-description">
                Pure electromagnetic pipeline performing logic through nonlinear wave mixing and quantum phase readouts. Handles latency-critical tasks like federated learning aggregation, real-time AI inference, and continuous optimization without digital bottlenecks.
              </p>
              <div className="tech-specs">
                <div className="tech-spec">
                  <div className="tech-spec-label">Latency</div>
                  <div className="tech-spec-value">ns</div>
                </div>
                <div className="tech-spec">
                  <div className="tech-spec-label">Parallelism</div>
                  <div className="tech-spec-value">Infinite</div>
                </div>
                <div className="tech-spec">
                  <div className="tech-spec-label">Medium</div>
                  <div className="tech-spec-value">EM Fields</div>
                </div>
              </div>
            </div>
          </div>

          <div className="substrate-content">
            <div className="substrate-text">
              <h3>The Combinatorial Electromagnetic Compute Substrate</h3>
              <p>
                LightFold's breakthrough innovation transforms the electromagnetic spectrum itself into a computational medium. Instead of routing discrete packets, client devices emit precisely modulated waveforms that superimpose in physical space.
              </p>
              <p>
                The "computation"—such as aggregating gradients from a billion federated learning nodes—occurs instantaneously as an emergent property of the interference pattern. The aggregator's role shifts from computing to decoding the result from the field's geometric structure.
              </p>
              <p>
                This approach transforms every connected device into a neuron in a planetary-scale brain, with the electromagnetic spectrum serving as its distributed nervous system.
              </p>
            </div>
            <div className="substrate-visual">
              <div className="substrate-diagram">
                {/* Placeholder for technical diagram */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="section fade-in">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">Impact Horizon</div>
            <h2 className="section-title">To Be of Historical Consequence</h2>
            <p className="section-description">
              The ultimate measure of any technology is the class of previously intractable problems it renders solvable
            </p>
          </div>

          <div className="vision-grid">
            <div className="vision-card fade-in">
              <div className="vision-number">01</div>
              <h4 className="vision-title">Fluid AI & Continuous Intelligence</h4>
              <p className="vision-description">
                Artificial intelligence that reasons about the continuous, analog world without digitization bottlenecks. Native support for differential equations, continuous optimization, and infinite-dimensional function spaces enables more intuitive and powerful general intelligence.
              </p>
            </div>

            <div className="vision-card fade-in">
              <div className="vision-number">02</div>
              <h4 className="vision-title">Planetary Climate Modeling</h4>
              <p className="vision-description">
                Real-time, molecular-level climate simulations spanning planetary scales. Continuous field computations enable perfect modeling of atmospheric dynamics, ocean currents, and ecosystem interactions for precise climate intervention strategies.
              </p>
            </div>

            <div className="vision-card fade-in">
              <div className="vision-number">03</div>
              <h4 className="vision-title">Quantum-Perfect Drug Discovery</h4>
              <p className="vision-description">
                Computational power to simulate protein folding, molecular interactions, and cellular dynamics at full quantum fidelity. Diseases like Alzheimer's, cancer, and aging become solvable engineering problems rather than biological mysteries.
              </p>
            </div>

            <div className="vision-card fade-in">
              <div className="vision-number">04</div>
              <h4 className="vision-title">Post-Scarcity Resource Allocation</h4>
              <p className="vision-description">
                Global logistics, materials science, and resource optimization simulated with perfect fidelity. Real-time optimization of supply chains, energy grids, and production systems creates circular economies with zero waste.
              </p>
            </div>

            <div className="vision-card fade-in">
              <div className="vision-number">05</div>
              <h4 className="vision-title">Consciousness & Cognitive Architecture</h4>
              <p className="vision-description">
                Understanding consciousness through continuous field dynamics rather than discrete neural networks. Enables development of truly conscious AI systems and breakthrough treatments for neurological conditions.
              </p>
            </div>

            <div className="vision-card fade-in">
              <div className="vision-number">06</div>
              <h4 className="vision-title">Fundamental Physics Simulation</h4>
              <p className="vision-description">
                First-principles simulation of space-time, quantum fields, and gravitational dynamics. Accelerates discovery in theoretical physics, enabling breakthrough technologies in energy, propulsion, and matter manipulation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Protocol Section */}
      <section id="protocol" className="section section-alt fade-in">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">Ethical Framework</div>
            <h2 className="section-title">The Nairobi Protocol</h2>
            <p className="section-description">
              A technology of this magnitude cannot exist without an inviolable ethical constitution governing its development and deployment
            </p>
          </div>

          <div className="protocol-grid">
            <div className="protocol-card fade-in">
              <div className="protocol-number">1</div>
              <h3 className="protocol-title">Universal Uplift Principle</h3>
              <p className="protocol-description">
                Every application of angalifu technology must demonstrably lift at least one million people from cognitive, economic, or resource scarcity. We measure success not by efficiency gains, but by human flourishing enabled.
              </p>
            </div>

            <div className="protocol-card fade-in">
              <div className="protocol-number">2</div>
              <h3 className="protocol-title">Radical Openness Mandate</h3>
              <p className="protocol-description">
                All non-core intellectual property, scientific models, verification frameworks, and educational resources will be open-sourced under perpetual public license to accelerate global innovation and prevent technological colonialism.
              </p>
            </div>

            <div className="protocol-card fade-in">
              <div className="protocol-number">3</div>
              <h3 className="protocol-title">Ecological Regeneration Imperative</h3>
              <p className="protocol-description">
                Every computational act must result in net-positive ecological impact through our "Noise as Fuel" paradigm. Computation becomes regenerative, harvesting environmental energy rather than consuming it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section fade-in">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">Partnership</div>
            <h2 className="section-title">Join the Transformation</h2>
            <p className="section-description">
              Building the future requires visionary partners who understand that true innovation transcends quarterly metrics
            </p>
          </div>

          <div className="contact-content">
            <p className="contact-description">
              Angalifu is assembling a multi-generational coalition of physicists, engineers, formal methods experts, and strategic visionaries to architect this computational future. We seek partnerships with sovereign wealth funds, national research institutions, and foundational philanthropies that possess both the vision and patience required to fund a project of genuine historical consequence.
            </p>
            <div className="contact-actions">
              <Link to="/contact" className="hero-cta">
                Initiate Partnership Dialogue
                <span>→</span>
              </Link>
              <Link to="/research/papers" className="hero-cta-secondary">
                Download Technical Whitepaper
                <span>↓</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
